/**
 * 商品詳細ページ
 * 各種スライダー、アコーディオン、フローティングUI、スティッキー表示などの制御
 */

import Splide from "@splidejs/splide";
import { breakpoint } from "../constant/breakpoint";
import { initAccordion } from "../util/accordionHelpers";
import { initProductSlider, initProgressSlider } from "../util/sliderHelpers";

/**
 * ======================================
 * プログレスバースライダー
 * ======================================
 */

/**
 * Buy Togetherスライダーを初期化
 */
const initBuyTogether = (): void => {
  initProgressSlider(
    "[data-progress-slider='buy-together']",
    "[data-progress-slider-pagination]",
    {
      type: "loop",
      perPage: 1,
      perMove: 1,
      gap: "16px",
      pagination: true,
      arrows: true,
      autoplay: false,
      speed: 800,
      rewindSpeed: 800,
      pauseOnHover: true,
      pauseOnFocus: true,
      fixedWidth: "358px",
      focus: "center",
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          fixedWidth: "100%",
        },
      },
    },
  );
};

/**
 * まとめ買いスライダーを初期化
 */
const initBulkBuySet = (): void => {
  initProgressSlider(
    "[data-progress-slider='bulk-buy']",
    "[data-progress-slider-pagination]",
    {
      type: "loop",
      perPage: 1,
      perMove: 1,
      gap: "16px",
      pagination: true,
      arrows: true,
      autoplay: false,
      speed: 800,
      rewindSpeed: 800,
      pauseOnHover: true,
      pauseOnFocus: true,
      fixedWidth: "358px",
      focus: "center",
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          fixedWidth: "100%",
        },
      },
    },
  );
};

/**
 * おすすめ単品商品スライダーを初期化
 */
const initRecommendedSingleProducts = (): void => {
  initProgressSlider(
    "[data-progress-slider='recommended-single']",
    "[data-progress-slider-pagination]",
    {
      type: "loop",
      perPage: 1,
      perMove: 1,
      gap: "16px",
      pagination: true,
      arrows: true,
      autoplay: false,
      speed: 800,
      rewindSpeed: 800,
      pauseOnHover: true,
      pauseOnFocus: true,
      fixedWidth: "358px",
      focus: "center",
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          fixedWidth: "100%",
        },
      },
    },
  );
};

/**
 * その他セット組商品スライダーを初期化
 */
const initOtherSetProducts = (): void => {
  initProgressSlider(
    "[data-progress-slider='other-set-products']",
    "[data-progress-slider-pagination]",
    {
      type: "loop",
      perPage: 1,
      perMove: 1,
      gap: "16px",
      pagination: true,
      arrows: true,
      autoplay: false,
      speed: 800,
      rewindSpeed: 800,
      pauseOnHover: true,
      pauseOnFocus: true,
      fixedWidth: "358px",
      focus: "center",
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          fixedWidth: "100%",
        },
      },
    },
  );
};

/**
 * ======================================
 * 2段階アコーディオン
 * ======================================
 */

/**
 * 商品説明アコーディオンを初期化
 */
const initDescription = (): void => {
  initAccordion("[data-accordion='description']", {
    headerClass: "c-product-accordion__header",
    viewMoreBtnClass: "c-product-view-more-btn",
    toggleBtnClass: "c-product-accordion__toggle-btn",
    innerClass: "c-product-accordion__inner",
  });
};

/**
 * 商品仕様・サイズアコーディオンを初期化
 */
const initSpecifications = (): void => {
  initAccordion("[data-accordion='specifications']", {
    headerClass: "c-product-accordion__header",
    viewMoreBtnClass: "c-product-view-more-btn",
    toggleBtnClass: "c-product-accordion__toggle-btn",
    innerClass: "c-product-accordion__inner",
    contentClass: "specifications-content",
  });
};

/**
 * ユーザーレビューアコーディオンを初期化
 */
const initReview = (): void => {
  initAccordion("[data-accordion='review']", {
    headerClass: "c-product-accordion__header",
    viewMoreBtnClass: "c-product-view-more-btn",
    toggleBtnClass: "c-product-accordion__toggle-btn",
    innerClass: "c-product-accordion__inner",
  });
};

/**
 * レビューサマリーをクリックした際にレビューアコーディオンを開いてスクロール
 */
/* 修正: review-summary-container クリックで #product-review アコーディオンを開く（PC/SP両対応・イベント委譲） */
const initReviewSummaryClick = (): void => {
  const summaryContainer = document.querySelector(
    ".review-summary-container",
  ) as HTMLElement;
  if (!summaryContainer) return;

  summaryContainer.addEventListener("click", () => {
    // PC用・SP用の2つの #product-review があるため、現在表示中のものを取得
    const reviewAccordions = document.querySelectorAll<HTMLElement>(
      "[data-accordion='review']",
    );
    let targetAccordion: HTMLElement | null = null;
    for (const accordion of Array.from(reviewAccordions)) {
      if (
        accordion.offsetParent !== null ||
        getComputedStyle(accordion).display !== "none"
      ) {
        targetAccordion = accordion;
        break;
      }
    }
    if (!targetAccordion) return;

    const header = targetAccordion.querySelector(
      ".c-product-accordion__header",
    ) as HTMLElement;
    if (!header) return;

    const scrollToReview = () => {
      const headerHeight =
        document.querySelector("header")?.getBoundingClientRect().height ?? 0;
      const offsetTop =
        (targetAccordion?.getBoundingClientRect().top ?? 0) +
        window.pageYOffset -
        headerHeight -
        20;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    };

    const isOpen = targetAccordion.classList.contains("is-open");
    if (!isOpen) {
      header.click();
      // 修正: アコーディオンの max-height トランジション(0.4s)完了後にスクロール
      setTimeout(scrollToReview, 420);
    } else {
      scrollToReview();
    }
  });
};

/**
 * ======================================
 * Floating - フローティングナビゲーションとカートボタン
 * ======================================
 */

/**
 * フローティングナビゲーションとカートボタンを初期化
 */
const initFloating = (): void => {
  const floatingNav = document.querySelector(
    "[data-floating-nav]",
  ) as HTMLElement;
  const floatingCartBtn = document.querySelector(
    "[data-floating-cart-btn]",
  ) as HTMLElement;
  const trigger = document.querySelector(
    "[data-floating-trigger]",
  ) as HTMLElement;

  // フローティングナビゲーションもカートボタンもない場合は何もしない
  if (!floatingNav && !floatingCartBtn) {
    return;
  }

  // ヘッダー要素を取得してCSS変数に高さを設定
  const header = document.querySelector(".p-header-wrapper") as HTMLElement;
  if (header) {
    const setHeaderHeight = () => {
      const headerHeight = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--header-height",
        `${headerHeight}px`,
      );
    };
    setHeaderHeight();
    // リサイズ時とスクロール時にも更新（追従時にヘッダーの高さが変わるため）
    window.addEventListener("resize", setHeaderHeight);
    window.addEventListener("scroll", setHeaderHeight);
  }

  // スクロールイベントで購入ボタンエリアがヘッダーに被るくらいビューポートから外れたかを判定
  const checkScrollPosition = () => {
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const headerHeight = header ? header.getBoundingClientRect().height : 0;

    // data-floating-trigger要素のbottomがヘッダー高さ+20px以下になったら表示（少し早めに発火）
    if (triggerRect.top <= headerHeight) {
      if (floatingNav) {
        floatingNav.classList.add("is-visible");
      }
      if (floatingCartBtn) {
        floatingCartBtn.classList.add("is-visible");
      }
    } else {
      // data-floating-trigger要素がまだ見えている場合は非表示
      if (floatingNav) {
        floatingNav.classList.remove("is-visible");
      }
      if (floatingCartBtn) {
        floatingCartBtn.classList.remove("is-visible");
      }
    }
  };

  if (!trigger) {
    // data-floating-trigger要素がない場合は非表示のまま
    console.error(
      "[data-floating-trigger] element not found. Floating elements will not be displayed.",
    );
    return;
  }

  // スクロールイベントを監視
  window.addEventListener("scroll", checkScrollPosition);
  // 初期チェック
  checkScrollPosition();

  // フローティングナビゲーションのスムーススクロール
  if (floatingNav) {
    const navItems = floatingNav.querySelectorAll("[data-nav-item]");

    // ページ内にアンカー先が存在しない項目を非表示にする
    for (const item of Array.from(navItems)) {
      const href = item.getAttribute("href");
      if (!href) {
        (item as HTMLElement).style.display = "none";
        continue;
      }
      const target =
        (document.querySelector(`${href}.p-product-sp-only`) as HTMLElement) ||
        (document.querySelector(href) as HTMLElement);
      if (!target) {
        (item as HTMLElement).style.display = "none";
      }
    }
    for (const item of Array.from(navItems)) {
      item.addEventListener("click", (e: Event) => {
        e.preventDefault();
        const href = item.getAttribute("href");
        if (!href) return;

        // SP用の要素を優先的に取得（.p-product-sp-onlyクラスを持つ要素）
        const target =
          (document.querySelector(
            `${href}.p-product-sp-only`,
          ) as HTMLElement) || (document.querySelector(href) as HTMLElement);
        if (target) {
          const offsetTop =
            target.getBoundingClientRect().top + window.pageYOffset - 150; // ヘッダーとフローティングナビの高さ分を考慮
          window.scrollTo({
            top: offsetTop,
            behavior: "smooth",
          });

          // アクティブ状態を更新
          for (const nav of Array.from(navItems)) {
            nav.classList.remove("p-product-floating-nav__item--active");
          }
          item.classList.add("p-product-floating-nav__item--active");
        }
      });
    }

    // スクロール位置に応じてアクティブな項目を更新
    window.addEventListener("scroll", () => {
      const sections: HTMLElement[] = [];
      for (const item of Array.from(navItems)) {
        const href = item.getAttribute("href");
        if (href) {
          // SP用の要素を優先的に取得
          const section =
            (document.querySelector(
              `${href}.p-product-sp-only`,
            ) as HTMLElement) || (document.querySelector(href) as HTMLElement);
          if (section) {
            sections.push(section);
          }
        }
      }

      const scrollPos = window.pageYOffset + 150; // オフセットを調整（ヘッダー+フローティングナビ分）
      sections.forEach((section, index) => {
        const top = section.offsetTop;
        const bottom = top + section.offsetHeight;
        if (scrollPos >= top && scrollPos < bottom) {
          for (const nav of Array.from(navItems)) {
            nav.classList.remove("p-product-floating-nav__item--active");
          }
          navItems[index].classList.add("p-product-floating-nav__item--active");
        }
      });
    });
  }

  // フローティングカートボタンのクリックイベント
  if (floatingCartBtn) {
    const floatingAddToCartBtn = floatingCartBtn.querySelector(
      "[data-floating-add-to-cart]",
    ) as HTMLButtonElement;
    // フォームクラスは Dawn デフォルトの "form" のため data 属性で取得
    const mainProductForm = document.querySelector(
      "[data-type='add-to-cart-form']",
    ) as HTMLFormElement;

    if (floatingAddToCartBtn && mainProductForm) {
      floatingAddToCartBtn.addEventListener("click", () => {
        // フォームを直接submitして選択中のバリアントをカートに追加
        const submitButton = mainProductForm.querySelector(
          "button[type='submit']",
        ) as HTMLButtonElement;
        if (submitButton && !submitButton.disabled) {
          submitButton.click();
        }
      });
    }
  }
};

/**
 * ======================================
 * Info Sticky - 商品情報のスティッキー表示
 * ======================================
 */

/**
 * 商品情報のスティッキー表示を初期化
 */
const initInfoSticky = (): void => {
  // 実際のHTML構造に合わせてセレクタを変更
  // product__info-wrapperが親要素で、その中にdata-product-info-sectionがある
  const infoContainer = document.querySelector(
    "[data-product-info-section]",
  ) as HTMLElement;
  const quantityForm = document.querySelector(
    "[data-product-info-quantity]",
  ) as HTMLElement;

  if (!infoContainer || !quantityForm) {
    console.warn("Product info sticky: Elements not found", {
      infoContainer: !!infoContainer,
      quantityForm: !!quantityForm,
    });
    return;
  }

  // topプロパティを計算して設定
  const updateStickyTop = (): void => {
    if (window.innerWidth < Number.parseInt(breakpoint.md)) {
      // SP時はstickyを解除
      infoContainer.style.position = "";
      infoContainer.style.top = "";
      return;
    }

    // 数量カウンターの位置を取得
    const quantityRect = quantityForm.getBoundingClientRect();
    const containerRect = infoContainer.getBoundingClientRect();

    // 数量カウンターのコンテナ内での相対位置を計算
    const quantityTopInContainer = quantityRect.top - containerRect.top;

    // ヘッダーの高さを考慮（固定ヘッダーがある場合）
    const headerWrapper = document.querySelector("header") as HTMLElement;
    const headerHeight = headerWrapper
      ? headerWrapper.getBoundingClientRect().height
      : 0;
    // topの値を計算: -(コンテナ上端から数量カウンターまでの距離) + ヘッダーの高さ + 余白
    // これにより、数量カウンター部分が画面上部に固定される
    const stickyTop = -quantityTopInContainer + headerHeight + 10;

    infoContainer.style.position = "sticky";
    infoContainer.style.top = `${stickyTop}px`;
  };

  // 初期実行（少し遅延させてDOMのレンダリングを待つ）
  setTimeout(() => {
    updateStickyTop();
  }, 100);

  // リサイズ時に再計算（デバウンス付き）
  let resizeTimeout: ReturnType<typeof setTimeout>;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateStickyTop, 100);
  });

  // スクロール時にも再計算（レイアウトシフトに対応）
  let scrollTimeout: ReturnType<typeof setTimeout>;
  window.addEventListener("scroll", () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(updateStickyTop, 50);
  });
};

/**
 * ======================================
 * Recently Viewed Bottom - 最近チェックした商品スライダー（商品詳細ページ下部用）
 * ======================================
 */

const STORAGE_KEY = "recentlyViewedProducts";

interface RecentlyViewedProduct {
  handle: string;
  url: string;
  title: string;
  image: string;
  price: string;
  compareAtPrice?: string;
  priceVaries?: boolean;
  addedAt: number;
  id?: string;
  variantId?: string;
  variants?: string;
}

/**
 * LocalStorageから閲覧履歴を取得
 */
const getRecentlyViewedProducts = (): RecentlyViewedProduct[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];

    const products = JSON.parse(data) as RecentlyViewedProduct[];

    // 有効な商品のみをフィルタリング
    const validProducts = products.filter(
      (product): product is RecentlyViewedProduct => {
        return (
          typeof product === "object" &&
          product !== null &&
          "handle" in product &&
          "url" in product &&
          "title" in product &&
          "addedAt" in product
        );
      },
    );

    // 現在の商品を除外
    const currentProductHandle = getCurrentProductHandle();
    const filteredProducts = validProducts.filter(
      (product) => product.handle !== currentProductHandle,
    );

    // 新しい順にソート
    return filteredProducts.sort((a, b) => b.addedAt - a.addedAt);
  } catch {
    return [];
  }
};

/**
 * 現在の商品ハンドルを取得
 */
const getCurrentProductHandle = (): string => {
  const path = window.location.pathname;
  const match = path.match(/\/products\/([^/]+)/);
  return match ? match[1] : "";
};

/**
 * 商品カードHTMLを生成
 */
const createProductCard = (
  product: RecentlyViewedProduct,
): HTMLElement | null => {
  const div = document.createElement("div");
  div.className = "splide__slide";

  // 価格をフォーマット（¥記号を削除して数値のみにする）
  const price = product.price.replace("¥", "").trim();
  const compareAtPrice = product.compareAtPrice
    ? product.compareAtPrice.replace("¥", "").trim()
    : "";

  // セール価格の判定
  const hasComparePrice =
    typeof product.compareAtPrice === "string" &&
    product.compareAtPrice !== "" &&
    product.compareAtPrice.trim() !== "";

  // 価格バリエーションの判定
  const priceVaries = product.priceVaries === true;
  const tilde = priceVaries ? "~" : "";

  // 価格表示部分のHTMLを生成
  let priceHTML = "";
  if (hasComparePrice) {
    // セール価格の場合
    priceHTML = `
      <div class="c-product-card__price-wrapper">
        <p class="c-product-card__price c-product-card__price--sale"><span class="c-product-card__price-symbol">¥</span>${price}${tilde}</p>
        <p class="c-product-card__price c-product-card__price--compare"><span class="c-product-card__price-symbol">¥</span>${compareAtPrice}</p>
      </div>
    `;
  } else {
    // 通常価格の場合
    priceHTML = `<p class="c-product-card__price"><span class="c-product-card__price-symbol">¥</span>${price}${tilde}</p>`;
  }

  div.innerHTML = `
    <div class="p-top-product-card__wrapper">
      <a href="${product.url}" class="p-top-product-card__link">
        <div class="p-top-product-card__img-wrapper">
          ${
            product.image
              ? `<img
                  src="${product.image}"
                  alt="${product.title}"
                  class="p-top-product-card__img"
                  width="257"
                  height="257"
                  loading="lazy"
                  decoding="async"
                >`
              : `<div class="p-top-product-card__placeholder"><p>画像なし</p></div>`
          }
        </div>
        <div class="p-top-product-card__info">
          <p class="p-top-product-card__title">${product.title}</p>
          <div class="c-product-card__price-favorite-wrapper">
            ${priceHTML}
            <div class="storecrm-favorite" data-product-id="${product.id || ""}" data-variant-id="${
              product.variantId || ""
            }" data-no-label data-variant-selector data-variants='${product.variants || "[]"}' data-product-handle="${
              product.handle || ""
            }"></div>
          </div>
        </div>
      </a>
    </div>
  `;

  return div;
};

/**
 * 最近チェックした商品スライダーを初期化
 */
const initRecentlyViewedBottom = (): void => {
  const parentSection = document.querySelector(
    ".p-product-recently-viewed",
  ) as HTMLElement;
  const recentlyViewedSlider = document.querySelector(
    "[data-splide-recently-viewed]",
  ) as HTMLElement;
  const container = document.querySelector(
    "[data-recently-viewed-container]",
  ) as HTMLElement;

  if (!parentSection || !recentlyViewedSlider || !container) {
    return;
  }

  // LocalStorageから閲覧履歴を取得
  const recentlyViewedProducts = getRecentlyViewedProducts();

  if (recentlyViewedProducts.length === 0) {
    // 閲覧履歴がない場合は非表示
    parentSection.style.display = "none";
    return;
  }

  // 商品カードを生成して挿入
  for (const product of recentlyViewedProducts) {
    const card = createProductCard(product);
    if (card) {
      container.appendChild(card);
    }
  }

  // Splideの初期化
  new Splide(recentlyViewedSlider, {
    type: "slide",
    perPage: 5,
    perMove: 1,
    gap: "16px",
    pagination: false,
    arrows: true,
    breakpoints: {
      [Number.parseInt(breakpoint.md) - 1]: {
        perPage: 2,
        gap: "8px",
        arrows: false,
      },
    },
  }).mount();
};

/**
 * ======================================
 * Recommend - おすすめ商品スライダー
 * ======================================
 */

/**
 * おすすめ商品スライダーを初期化
 */
const initRecommend = (): void => {
  initProductSlider("[data-product-slider='recommend']", {
    breakpoints: {
      [Number.parseInt(breakpoint.md) - 1]: {
        perPage: 2,
        gap: "8px",
        arrows: false,
      },
    },
  });
};

/**
 * ======================================
 * Related Recipes - 関連レシピスライダー
 * ======================================
 */

/**
 * 関連レシピスライダーを初期化
 */
const initRelatedRecipes = (): void => {
  const sliderElements = document.querySelectorAll<HTMLElement>(
    "[data-splide-recipes]",
  );

  for (const element of Array.from(sliderElements)) {
    const splide = new Splide(element, {
      type: "slide",
      perPage: 3,
      perMove: 1,
      gap: "16px",
      pagination: false,
      arrows: true,
      drag: true,
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          perPage: 2.3,
          gap: "16px",
          arrows: false,
          padding: { left: 0, right: 0 },
        },
      },
    });

    splide.mount();
  }
};

/**
 * ======================================
 * メタ連動バンドルバリエーション（ラジオの active クラス同期）
 * ======================================
 */

/**
 * メタリスト選択をギャラーへ通知（ギャラー内メディア or URL プレビュー）
 */
const notifyMetafieldBundleGallery = (input: HTMLInputElement): void => {
  // 修正: 「選択しない」時はメタ参照プレビューを閉じて本体ギャラリー表示へ
  if (input.dataset.metafieldBundleNone === "true") {
    document.dispatchEvent(
      new CustomEvent("metafield-bundle:gallery-media", {
        bubbles: true,
        detail: { resetGallery: true },
      }),
    );
    return;
  }
  const mediaId = input.dataset.variantFeaturedMediaId?.trim() ?? "";
  const previewSrcPc = input.dataset.variantPreviewSrcPc?.trim() ?? "";
  const previewSrcSp = input.dataset.variantPreviewSrcSp?.trim() ?? "";
  const previewAlt = input.dataset.variantPreviewAlt ?? "";
  if (mediaId === "" && previewSrcPc === "" && previewSrcSp === "") return;

  document.dispatchEvent(
    new CustomEvent("metafield-bundle:gallery-media", {
      bubbles: true,
      detail: {
        ...(mediaId !== "" ? { mediaId } : {}),
        ...(previewSrcPc !== "" ? { previewSrcPc } : {}),
        ...(previewSrcSp !== "" ? { previewSrcSp } : {}),
        previewAlt,
      },
    }),
  );
};

/**
 * メタリストのバリエーションラジオで label の active を同期する
 */
const initMetafieldBundleVariantUi = (): void => {
  const roots = document.querySelectorAll<HTMLElement>("[data-metafield-bundle-root]");
  for (const root of Array.from(roots)) {
    const syncActive = (): void => {
      for (const input of Array.from(
        root.querySelectorAll<HTMLInputElement>("input.p-product-info__variant-radio"),
      )) {
        const label = input.nextElementSibling;
        if (label instanceof HTMLLabelElement) {
          label.classList.toggle("p-product-info__variant--active", input.checked);
        }
      }
    };
    root.addEventListener("change", (event) => {
      syncActive();
      // 修正: 選択した参照バリアントのメディアIDでメインギャラリーを更新
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== "radio") return;
      const nameAttr = target.getAttribute("name");
      if (nameAttr == null || !nameAttr.startsWith("metafield-bundle-option-")) return;
      notifyMetafieldBundleGallery(target);
    });
    syncActive();
  }
};

/**
 * ======================================
 * メインエクスポート関数
 * ======================================
 */

/**
 * 商品詳細ページの全機能を初期化
 */
export const product = (): void => {
  // 一緒に購入スライダーを初期化
  initBuyTogether();
  // まとめ買いスライダーを初期化
  initBulkBuySet();
  // おすすめ単品商品スライダーを初期化
  initRecommendedSingleProducts();
  // その他セット組商品スライダーを初期化
  initOtherSetProducts();
  // 商品説明アコーディオンを初期化
  initDescription();
  // 商品仕様・サイズアコーディオンを初期化
  initSpecifications();
  // ユーザーレビューアコーディオンを初期化
  initReview();
  // レビューサマリークリックでレビューアコーディオンを開く
  initReviewSummaryClick();
  // 関連レシピスライダーを初期化
  initRelatedRecipes();
  // おすすめ商品スライダーを初期化
  initRecommend();
  // 最近チェックした商品スライダーを初期化
  initRecentlyViewedBottom();
  // フローティングナビゲーションとカートボタンを初期化
  initFloating();
  // 商品情報のスティッキー表示を初期化
  initInfoSticky();
  // 修正: メタ連動バンドル用バリエーション UI
  initMetafieldBundleVariantUi();
};
